import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSessionData, setSession } from "../utils/auth";
import { markWelcomeIntroSeen } from "../core/users";

// Bump this string when the popup content changes substantially
// and you want to re-show it to everyone (logged-in or anonymous).
export const WELCOME_INTRO_VERSION = "v1";

const STORAGE_KEY = "wefayo_welcome_intro_version";
const STORAGE_KEY_SEEN_AT = "wefayo_welcome_intro_seen_at";
const FORCE_PARAM = "welcomeIntro";

// Routes where the popup should NOT show (auth flows etc.)
const SKIP_PATH_PREFIXES = [
    "/login",
    "/register",
    "/activate",
    "/activate-new-email",
    "/forgot-password",
    "/reset-password",
    "/join",
];

const matchesSkipPath = (pathname) => {
    return SKIP_PATH_PREFIXES.some(
        (p) => pathname === p || pathname.startsWith(p + "/")
    );
};

const isForcedOpen = (search) => {
    try {
        const params = new URLSearchParams(search);
        const value = params.get(FORCE_PARAM);
        return value === "force" || value === "1" || value === "true";
    } catch {
        return false;
    }
};

const hasSeenCurrentVersion = () => {
    let seenInLocalStorage = false;
    try {
        seenInLocalStorage =
            localStorage.getItem(STORAGE_KEY) === WELCOME_INTRO_VERSION;
    } catch {
        seenInLocalStorage = false;
    }

    const user = getSessionData();
    if (user) {
        // Either the server flag or this-browser dismissal counts as seen.
        // (localStorage backup avoids a brief re-open while the API call
        // to persist the dismissal is still in flight.)
        return (
            user.seen_welcome_intro_version === WELCOME_INTRO_VERSION ||
            seenInLocalStorage
        );
    }
    return seenInLocalStorage;
};

const stripForceParamFromUrl = () => {
    if (typeof window === "undefined" || !window.history?.replaceState) return;
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(FORCE_PARAM)) return;
        url.searchParams.delete(FORCE_PARAM);
        const newSearch = url.searchParams.toString();
        const newUrl =
            url.pathname + (newSearch ? `?${newSearch}` : "") + url.hash;
        window.history.replaceState({}, "", newUrl);
    } catch {
        // ignore
    }
};

export const useWelcomeIntro = () => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (matchesSkipPath(location.pathname)) {
            setIsOpen(false);
            return;
        }

        if (isForcedOpen(location.search)) {
            setIsOpen(true);
            return;
        }

        setIsOpen(!hasSeenCurrentVersion());
    }, [location.pathname, location.search]);

    const dismiss = useCallback(async () => {
        setIsOpen(false);

        // Drop the ?welcomeIntro=force param so it doesn't immediately
        // re-open the popup on the next route change.
        stripForceParamFromUrl();

        // Always write localStorage so the same browser remembers,
        // even if the user later logs out.
        try {
            localStorage.setItem(STORAGE_KEY, WELCOME_INTRO_VERSION);
            localStorage.setItem(STORAGE_KEY_SEEN_AT, new Date().toISOString());
        } catch {
            // storage unavailable (private mode etc.) — ignore
        }

        // If logged in, persist the flag on the user record so it
        // follows them across devices/browsers.
        const user = getSessionData();
        if (user) {
            try {
                const response = await markWelcomeIntroSeen(WELCOME_INTRO_VERSION);
                if (response && !(response instanceof Error)) {
                    const updatedUser = {
                        ...user,
                        seen_welcome_intro_version:
                            response.seen_welcome_intro_version || WELCOME_INTRO_VERSION,
                    };
                    setSession(updatedUser);
                }
            } catch (err) {
                console.warn("Failed to persist welcome-intro seen flag", err);
            }
        }
    }, []);

    return { isOpen, dismiss };
};

export default useWelcomeIntro;
